import type { RateCard } from "@rebuild/shared/models";
import type {
  CreateRateCardRequest,
  CreateRateItemInput,
  ExtractRateItemsResponse,
  RateCardWithItems,
  RateItem,
  UpdateRateItemInput,
} from "./rateCards.types";
import RestAPI from "../../utils/restAPI";

const RATE_CARDS_ROUTE = "/rest/tools/rate-cards";

function withOrg(path: string, organizationId: string): string {
  const sep = path.includes("?") ? "&" : "?";
  return `${path}${sep}organizationId=${encodeURIComponent(organizationId)}`;
}

function requireResult<T>(result: T | null | undefined, action: string): T {
  if (result == null) {
    throw new Error(`Failed to ${action}`);
  }
  return result;
}

export async function extractRateItemsFromPdf(organizationId: string, file: File): Promise<ExtractRateItemsResponse> {
  const formData = new FormData();
  formData.append("file", file);
  return requireResult(
    await RestAPI.fetchRebuildAPI(withOrg(`${RATE_CARDS_ROUTE}/extract`, organizationId), "POST", formData, "data", true, false),
    "extract rate items from PDF",
  );
}

export async function listRateCards(organizationId: string): Promise<RateCard[]> {
  return requireResult(
    await RestAPI.fetchRebuildAPI(withOrg(RATE_CARDS_ROUTE, organizationId), "GET", {}, "data", true, false),
    "list rate cards",
  );
}

export async function createRateCard(organizationId: string, body: CreateRateCardRequest): Promise<RateCardWithItems> {
  return requireResult(
    await RestAPI.fetchRebuildAPI(withOrg(RATE_CARDS_ROUTE, organizationId), "POST", body, "data", true, false),
    "create rate card",
  );
}

export async function getRateCard(organizationId: string, rateCardId: string): Promise<RateCardWithItems> {
  return requireResult(
    await RestAPI.fetchRebuildAPI(withOrg(`${RATE_CARDS_ROUTE}/${rateCardId}`, organizationId), "GET", {}, "data", true, false),
    "load rate card",
  );
}

export async function createRateItem(organizationId: string, rateCardId: string, body: CreateRateItemInput): Promise<RateItem> {
  return requireResult(
    await RestAPI.fetchRebuildAPI(
      withOrg(`${RATE_CARDS_ROUTE}/${rateCardId}/rate-items`, organizationId),
      "POST",
      body,
      "data",
      true,
      false,
    ),
    "create rate item",
  );
}

export async function updateRateItem(
  organizationId: string,
  rateCardId: string,
  rateItemId: string,
  body: UpdateRateItemInput,
): Promise<RateItem> {
  return requireResult(
    await RestAPI.fetchRebuildAPI(
      withOrg(`${RATE_CARDS_ROUTE}/${rateCardId}/rate-items/${rateItemId}`, organizationId),
      "PATCH",
      body,
      "data",
      true,
      false,
    ),
    "update rate item",
  );
}

export async function deleteRateItem(organizationId: string, rateCardId: string, rateItemId: string): Promise<void> {
  requireResult(
    await RestAPI.fetchRebuildAPI(
      withOrg(`${RATE_CARDS_ROUTE}/${rateCardId}/rate-items/${rateItemId}`, organizationId),
      "DELETE",
      {},
      "success",
      true,
      false,
    ),
    "delete rate item",
  );
}
